import { useRef, useState } from "react";
import { createStudy, updateStudy, uploadStudyAsset, deleteStudy, studyUrl, getAdminToken } from "@/villa142/api";
import { ConfirmModal } from "@/villa142/admin/ConfirmModal";

export default function StudiesView({ studies, reload }) {
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRefs = useRef({});
  const timers = useRef({});
  const token = getAdminToken();

  const patch = (id, p) => {
    clearTimeout(timers.current[id]);
    timers.current[id] = setTimeout(async () => {
      await updateStudy(id, p);
      reload();
    }, 700);
  };

  const move = async (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= studies.length) return;
    await updateStudy(studies[idx].id, { order: t });
    await updateStudy(studies[t].id, { order: idx });
    reload();
  };

  const onFile = async (id, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await uploadStudyAsset(id, file);
    reload();
  };

  return (
    <div data-testid="v142-studies-view">
      <p className="v142-copy">
        The Visual Cues chapter is <strong>archived</strong> — these studies and any past responses are preserved
        here for the record, but nothing from this chapter is shown to the clients. Re-activating the chapter in the
        Questionnaire tab would bring active, imaged studies back into the flow.
      </p>
      <div className="v142-studies-grid">
        {studies.map((s, i) => (
          <div className="v142-study-card" key={s.id} data-testid={`v142-study-card-${s.id}`}>
            <div className="v142-study-thumb">
              {s.has_image ? (
                <img src={studyUrl(s.id, token)} alt={s.title} className="v142-study-img" />
              ) : (
                <div className="v142-study-ph"><span>Placeholder — image to add</span></div>
              )}
            </div>
            <input
              className="v142-input v142-input-small"
              aria-label="Study title"
              defaultValue={s.title}
              data-testid={`v142-study-title-${s.id}`}
              onChange={(e) => patch(s.id, { title: e.target.value })}
            />
            <input
              className="v142-input v142-input-small"
              aria-label="Study caption"
              placeholder="Caption (optional)"
              defaultValue={s.caption}
              onChange={(e) => patch(s.id, { caption: e.target.value })}
            />
            <div className="v142-study-ctrl">
              <button type="button" className="v142-link" aria-label="Move up" onClick={() => move(i, -1)}>↑</button>
              <button type="button" className="v142-link" aria-label="Move down" onClick={() => move(i, 1)}>↓</button>
              <button type="button" className="v142-link" data-testid={`v142-study-replace-${s.id}`} onClick={() => fileRefs.current[s.id]?.click()}>
                {s.has_image ? "Replace image" : "Add image"}
              </button>
              <button type="button" className="v142-link" onClick={async () => { await updateStudy(s.id, { active: !s.active }); reload(); }}>
                {s.active ? "Hide" : "Show"}
              </button>
              <button
                type="button"
                className="v142-link v142-danger-link"
                data-testid={`v142-study-delete-${s.id}`}
                onClick={() => setConfirm({ title: "Remove visual study", body: `Remove "${s.title}" and its image from the questionnaire?`, action: () => deleteStudy(s.id) })}
              >
                Remove
              </button>
            </div>
            <input ref={(el) => { fileRefs.current[s.id] = el; }} type="file" accept="image/*" className="v142-visually-hidden" onChange={(e) => onFile(s.id, e)} aria-label="Study image" />
          </div>
        ))}
      </div>
      {studies.length < 6 && (
        <button type="button" className="v142-add-row" data-testid="v142-add-study-btn" onClick={async () => { await createStudy(`Visual study ${String(studies.length + 1).padStart(2, "0")}`); reload(); }}>
          + Add a visual study
        </button>
      )}
      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel="Remove"
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => { setBusy(true); try { await confirm.action(); setConfirm(null); reload(); } finally { setBusy(false); } }}
      />
    </div>
  );
}
